"use client"

import { useEffect, useState } from "react"

import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

import type { ChartSeriesColor } from "../model/chart/types"
import {
  DEFAULT_CHART_HEIGHT,
  formatChartValue,
  MAX_PIE_SLICES,
  resolveSeriesFill,
  resolveSeriesMark,
  type ChartValueFormat,
  type CovalChartSpecV1,
} from "../utils/chartSpec"

/**
 * The recharts half of the Sofia chart card — a pure spec → SVG
 * mapping, lazy-loaded by CoviVizCard via next/dynamic so recharts stays out
 * of the panel's initial bundle. SECURITY INVARIANTS: everything model-
 * authored renders as React text or lands in SVG paint attributes through the
 * CLOSED maps in utils/chartSpec.ts (color enum → Coval tokens, format enum →
 * FE-owned formatters). No dangerouslySetInnerHTML, no function-valued or
 * free-form-CSS spec fields, ever.
 */

// SVG presentation attributes do not reliably resolve chained CSS variables, so
// the axis, grid, and label colors are read from the computed theme once the
// chart mounts and handed to recharts as concrete values.
const FALLBACK_THEME_PAINT = "currentColor"

function resolveThemePaint(styles: CSSStyleDeclaration, name: string): string {
  const visited = new Set<string>()
  let current = name

  while (!visited.has(current)) {
    visited.add(current)
    const value = styles.getPropertyValue(current).trim()
    const reference = value.match(/^var\((--[^,\s)]+)(?:,[^)]+)?\)$/)?.[1]
    if (!reference) {
      return value || FALLBACK_THEME_PAINT
    }
    current = reference
  }

  return FALLBACK_THEME_PAINT
}

function useThemePaint(lightTheme: boolean): { text: string; line: string } {
  const [paint, setPaint] = useState({
    text: FALLBACK_THEME_PAINT,
    line: FALLBACK_THEME_PAINT,
  })
  useEffect(() => {
    if (typeof window === "undefined") {
      return
    }
    // The PDF always has a light page, even when the viewer uses dark mode.
    // Read the same scoped semantic tokens used by the export chart container.
    const lightScope = lightTheme
      ? document.querySelector<HTMLElement>("[data-notebook-export-charts]")
      : null
    const styles = getComputedStyle(lightScope ?? document.documentElement)
    setPaint({
      text: resolveThemePaint(styles, "--coval-text-muted"),
      line: resolveThemePaint(styles, "--coval-border"),
    })
  }, [lightTheme])
  return paint
}
const TOOLTIP_CONTENT_STYLE = {
  backgroundColor: "var(--surface-default)",
  border: "1px solid var(--coval-border)",
  borderRadius: 6,
  fontSize: 12,
  color: "var(--coval-text)",
} as const

export interface CoviVizChartBodyProps {
  spec: CovalChartSpecV1
  /** Draw height in px — the inline card uses the default; wider surfaces pass more. */
  height?: number
  /** Override the spec's value_labels; the notebook turns them off and relies on hover. */
  showValueLabels?: boolean
  /** Ignore model-chosen series colors and use the app palette rotation. */
  paletteOnly?: boolean
  /** Report-only presentation control; preserve ordinary Sofia pie/stat colors. */
  reportColors?: boolean
  /** Disable transitions when rendering a saved chart for a document export. */
  animate?: boolean
  /** Use the light semantic token scope when painting a chart for PDF export. */
  lightTheme?: boolean
}

function seriesValuesOf(spec: CovalChartSpecV1): number[] {
  return spec.data.flatMap((row) =>
    spec.series
      .map((series) => row[series.field])
      .filter((value): value is number => typeof value === "number"),
  )
}

function seriesLabel(spec: CovalChartSpecV1, index: number): string {
  const series = spec.series[index]
  return series?.name ?? series?.field ?? "?"
}

/** Y-side value formatter: percent-stacked charts always read as percent. */
function yFormatOf(spec: CovalChartSpecV1): ChartValueFormat | undefined {
  return spec.opts?.percent_stack ? "percent" : spec.y?.format
}

function StatTile({
  spec,
  paletteOnly,
}: {
  spec: CovalChartSpecV1
  paletteOnly: boolean
}) {
  const series = spec.series[0]
  const value = series ? spec.data[0]?.[series.field] : undefined
  const label = series?.name ?? spec.y?.label
  return (
    <div
      data-testid="covi-viz-stat"
      className="flex flex-col items-center justify-center gap-1 py-8"
    >
      <span
        className="text-3xl font-semibold tabular-nums text-coval-text"
        style={
          series?.color && !paletteOnly
            ? { color: resolveSeriesMark(series.color, 0) }
            : undefined
        }
      >
        {formatChartValue(value, yFormatOf(spec))}
      </span>
      {label ? (
        <span className="text-xs text-coval-text-muted">{label}</span>
      ) : null}
    </div>
  )
}

interface PieSlice {
  name: string
  value: number
}

/** Rows → slices, capped at MAX_PIE_SLICES with the tail summed into "Other". */
function buildPieSlices(spec: CovalChartSpecV1): PieSlice[] {
  const xField = spec.x?.field
  const valueField = spec.series[0]?.field
  if (!xField || !valueField) {
    return []
  }
  const slices = spec.data.map((row) => {
    const raw = row[valueField]
    return {
      name: String(row[xField] ?? "—"),
      value: typeof raw === "number" && Number.isFinite(raw) ? raw : 0,
    }
  })
  if (slices.length <= MAX_PIE_SLICES) {
    return slices
  }
  const sorted = [...slices].sort((a, b) => b.value - a.value)
  const kept = sorted.slice(0, MAX_PIE_SLICES - 1)
  const other = sorted
    .slice(MAX_PIE_SLICES - 1)
    .reduce((sum, slice) => sum + slice.value, 0)
  return [...kept, { name: "Other", value: other }]
}

export function CoviVizChartBody({
  spec,
  height = DEFAULT_CHART_HEIGHT,
  showValueLabels,
  paletteOnly = false,
  reportColors = false,
  animate = true,
  lightTheme = false,
}: CoviVizChartBodyProps) {
  const paint = useThemePaint(lightTheme)
  if (spec.chart === "stat") {
    return <StatTile spec={spec} paletteOnly={paletteOnly || !reportColors} />
  }

  const AXIS_TICK = { fontSize: 11, fill: paint.text }
  const AXIS_LINE = { stroke: paint.line }
  const GRID_STROKE = paint.line
  const xField = spec.x?.field ?? ""
  const yFormat = yFormatOf(spec)
  const showLegend = spec.opts?.legend !== false
  const stacked =
    spec.opts?.stacked === true || spec.opts?.percent_stack === true
  const horizontal = spec.chart === "bar" && spec.opts?.horizontal === true
  const valueLabels = showValueLabels ?? spec.opts?.value_labels === true
  const seriesColor = (color: ChartSeriesColor | undefined) =>
    paletteOnly ? undefined : color
  const integerValues = seriesValuesOf(spec).every(Number.isInteger)
  const percentStack = spec.opts?.percent_stack === true

  const formatY = (value: unknown) => formatChartValue(value, yFormat)
  const formatXTick =
    spec.x?.kind === "time"
      ? (value: unknown) => formatChartValue(value, "date")
      : undefined
  // An unstacked series whose values are all equal collapses an auto domain to a point and draws zero-height bars; anchor it at zero so the bars have room.
  const seriesValues = seriesValuesOf(spec)
  const flatSeries =
    !stacked &&
    seriesValues.length > 0 &&
    seriesValues.every((value) => value === seriesValues[0])
  const flatValue = seriesValues[0]
  const flatDomain: [number, number] =
    flatValue < 0 ? [flatValue, 0] : [0, flatValue > 0 ? flatValue : 1]
  // Bars encode magnitude by length, so a non-negative bar chart must start at
  // zero; an automatic minimum turns 0.83 vs 0.76 into a full bar and a sliver.
  const zeroBasedBars =
    spec.chart === "bar" &&
    seriesValues.length > 0 &&
    seriesValues.every((value) => value >= 0)
  const yDomain: [number | "auto", number | "auto"] = percentStack
    ? [0, 1]
    : [
        spec.y?.min ??
          (flatSeries ? flatDomain[0] : zeroBasedBars ? 0 : "auto"),
        spec.y?.max ?? (flatSeries ? flatDomain[1] : "auto"),
      ]
  const tooltipFormatter = (value: unknown) => formatY(value)

  if (spec.chart === "pie") {
    const slices = buildPieSlices(spec)
    return (
      <ResponsiveContainer width="100%" height={height}>
        <PieChart role="img" aria-label={spec.title}>
          <Pie
            isAnimationActive={animate}
            data={slices}
            dataKey="value"
            nameKey="name"
            label={
              valueLabels
                ? (entry: PieSlice) => formatY(entry.value)
                : undefined
            }
          >
            {slices.map((slice, index) => (
              <Cell
                key={slice.name}
                fill={resolveSeriesFill(
                  reportColors ? seriesColor(spec.series[0]?.color) : undefined,
                  index,
                )}
              />
            ))}
          </Pie>
          <Tooltip
            formatter={tooltipFormatter}
            contentStyle={TOOLTIP_CONTENT_STYLE}
          />
          {showLegend ? <Legend wrapperStyle={{ fontSize: 11 }} /> : null}
        </PieChart>
      </ResponsiveContainer>
    )
  }

  if (spec.chart === "scatter") {
    return (
      <ResponsiveContainer width="100%" height={height}>
        <ScatterChart
          role="img"
          aria-label={spec.title}
          margin={{ top: 8, right: 8, bottom: 4, left: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
          <XAxis
            dataKey="x"
            type={spec.x?.kind === "number" ? "number" : "category"}
            name={spec.x?.label ?? xField}
            tick={AXIS_TICK}
            axisLine={AXIS_LINE}
            tickLine={AXIS_LINE}
            tickFormatter={formatXTick}
            allowDuplicatedCategory={false}
          />
          <YAxis
            dataKey="y"
            type="number"
            name={spec.y?.label}
            domain={yDomain}
            tick={AXIS_TICK}
            axisLine={AXIS_LINE}
            tickLine={AXIS_LINE}
            tickFormatter={formatY}
            width={56}
          />
          <Tooltip
            formatter={tooltipFormatter}
            contentStyle={TOOLTIP_CONTENT_STYLE}
            cursor={{ strokeDasharray: "3 3" }}
          />
          {showLegend ? <Legend wrapperStyle={{ fontSize: 11 }} /> : null}
          {spec.series.map((series, index) => (
            <Scatter
              isAnimationActive={animate}
              key={series.field}
              name={seriesLabel(spec, index)}
              data={spec.data.map((row) => ({
                x: row[xField],
                y: row[series.field],
              }))}
              fill={resolveSeriesMark(seriesColor(series.color), index)}
            />
          ))}
        </ScatterChart>
      </ResponsiveContainer>
    )
  }

  // bar / line / area share the cartesian frame.
  const stackId = stacked ? "stack" : undefined
  // recharts discovers axes, grid, tooltip, and legend by inspecting the
  // chart's direct children; anything wrapped in a Fragment is invisible to it
  // and silently never renders. Keep these as a flat keyed array.
  const axes = horizontal
    ? [
        <XAxis
          key="x"
          type="number"
          domain={yDomain}
          tick={AXIS_TICK}
          axisLine={AXIS_LINE}
          tickLine={AXIS_LINE}
          tickFormatter={formatY}
        />,
        <YAxis
          key="y"
          type="category"
          dataKey={xField}
          tick={AXIS_TICK}
          axisLine={AXIS_LINE}
          tickLine={AXIS_LINE}
          width={96}
        />,
      ]
    : [
        <XAxis
          key="x"
          dataKey={xField}
          tick={AXIS_TICK}
          axisLine={AXIS_LINE}
          tickLine={AXIS_LINE}
          tickFormatter={formatXTick}
          label={
            spec.x?.label
              ? {
                  value: spec.x.label,
                  position: "insideBottom",
                  offset: -4,
                  fontSize: 11,
                  fill: paint.text,
                }
              : undefined
          }
        />,
        <YAxis
          key="y"
          domain={yDomain}
          allowDecimals={!integerValues}
          tick={AXIS_TICK}
          axisLine={AXIS_LINE}
          tickLine={AXIS_LINE}
          tickFormatter={formatY}
          width={spec.y?.label ? 72 : 56}
          label={
            spec.y?.label
              ? {
                  value: spec.y.label,
                  angle: -90,
                  position: "insideLeft",
                  offset: 12,
                  style: { textAnchor: "middle" },
                  fontSize: 11,
                  fill: paint.text,
                }
              : undefined
          }
        />,
      ]
  const frameChildren = [
    <CartesianGrid key="grid" strokeDasharray="3 3" stroke={GRID_STROKE} />,
    ...axes,
    <Tooltip
      key="tooltip"
      formatter={tooltipFormatter}
      contentStyle={TOOLTIP_CONTENT_STYLE}
    />,
    ...(showLegend
      ? [<Legend key="legend" wrapperStyle={{ fontSize: 11 }} />]
      : []),
  ]
  const margin = { top: 8, right: 8, bottom: spec.x?.label ? 12 : 4, left: 0 }

  if (spec.chart === "line") {
    return (
      <ResponsiveContainer width="100%" height={height}>
        <LineChart
          role="img"
          aria-label={spec.title}
          data={spec.data}
          margin={margin}
        >
          {frameChildren}
          {spec.series.map((series, index) => (
            <Line
              isAnimationActive={animate}
              key={series.field}
              type="monotone"
              dataKey={series.field}
              name={seriesLabel(spec, index)}
              stroke={resolveSeriesMark(seriesColor(series.color), index)}
              strokeWidth={2}
              dot={false}
            >
              {valueLabels ? (
                <LabelList
                  dataKey={series.field}
                  position="top"
                  fontSize={10}
                  formatter={formatY}
                />
              ) : null}
            </Line>
          ))}
        </LineChart>
      </ResponsiveContainer>
    )
  }

  if (spec.chart === "area") {
    return (
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart
          role="img"
          aria-label={spec.title}
          data={spec.data}
          margin={margin}
          stackOffset={percentStack ? "expand" : undefined}
        >
          {frameChildren}
          {spec.series.map((series, index) => (
            <Area
              isAnimationActive={animate}
              key={series.field}
              type="monotone"
              dataKey={series.field}
              name={seriesLabel(spec, index)}
              stroke={resolveSeriesMark(seriesColor(series.color), index)}
              fill={resolveSeriesFill(seriesColor(series.color), index)}
              fillOpacity={0.25}
              stackId={stackId}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    )
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        role="img"
        aria-label={spec.title}
        data={spec.data}
        margin={margin}
        layout={horizontal ? "vertical" : "horizontal"}
        stackOffset={percentStack ? "expand" : undefined}
      >
        {frameChildren}
        {spec.series.map((series, index) => (
          <Bar
            isAnimationActive={animate}
            key={series.field}
            dataKey={series.field}
            name={seriesLabel(spec, index)}
            fill={resolveSeriesFill(seriesColor(series.color), index)}
            stackId={stackId}
            // A one- or two-category chart otherwise draws slabs as wide as the
            // card; capping the bar keeps it readable as a bar.
            maxBarSize={72}
          >
            {valueLabels ? (
              <LabelList
                dataKey={series.field}
                position={horizontal ? "right" : "top"}
                fontSize={10}
                formatter={formatY}
              />
            ) : null}
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  )
}
